import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, timer} from 'rxjs';
import {delay, expand, filter, finalize, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {ECCommunityComputationStatus, ECComputedCommunityEconomics} from 'smag-commons/dto/eco';
import {SmagApiService} from '../api/smag-api.service';

@Injectable({
    providedIn: 'root'
})
export class ComputationStatusService implements OnDestroy {

    private destroyed = new Subject<void>();
    private showComputationBanner: BehaviorSubject<boolean>;

    constructor(private apiService: SmagApiService) {
        this.showComputationBanner = new BehaviorSubject<boolean>(false);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    /**
     * Emits whenever a computation repeats.
     * This means it doesn't trigger if computation is not in progress.
     */
    get showComputingWarning(): Observable<boolean> {
        return this.showComputationBanner.asObservable();
    }

    // Calls API to know whether the ec economics computation is in progress, missing or finished.
    // If it's in progress, it waits 2000ms before calling API again (unless it's more than 15 mins old).
    // If the computation is complete (i.e. has "response.status" === ECCommunityComputationStatus.COMPUTED), it returns the response
    checkComputationStatus(entityId: string, year: number): Observable<ECComputedCommunityEconomics> {
        // Wait 500ms before starting
        return timer(500).pipe(
            switchMap(() => this.getEconomicsStatus(entityId, year).pipe(
                takeUntil(this.destroyed),
                expand(response => this.checkForStatusAndTimestamp(response) ? of(response) :
                    of(response).pipe(
                        delay(2000),
                        tap(() => this.showComputationBanner.next(true)),
                        switchMap(() => this.getEconomicsStatus(entityId, year).pipe(takeUntil(this.destroyed))))),
                filter(response => this.checkForStatusAndTimestamp(response)), // continue only if value is either computed or missing or old
                take(1),
                finalize(() => setTimeout(() => this.showComputationBanner.next(false), 2000)))) // after 2 sec, tell listeners to stop showing warning banner
        );
    }

    private getEconomicsStatus(entityId: string, year: number) {
        return this.apiService.ecEconomicsStatus(entityId, year);
    }

    // the computation is considered "ok" if:
    // - status is computed
    // - status is missing
    // - status is in progress or scheduled, but the timestamp is older than 15 minutes
    private checkForStatusAndTimestamp(response: ECComputedCommunityEconomics): boolean {
        return response.status === ECCommunityComputationStatus.COMPUTED ||
            response.status === ECCommunityComputationStatus.MISSING ||
            (response.status === ECCommunityComputationStatus.IN_PROGRESS || response.status === ECCommunityComputationStatus.SCHEDULED) && this.isTimestampOlderThan15Mins(response.started_on);
    }

    private isTimestampOlderThan15Mins(timestamp: number): boolean {
        return timestamp < (Date.now() - 900000);
    }
}
